import { EntratelForm, FormHeaderTitle, Infobox, IntermediaryData } from "@onefront/workspace-forms";
import { Box, Button, Stack, useTheme } from "@vapor/react-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { IntermediaryListDto } from "../../../../../../../@types/dtos/intermediaries/IntermediaryListDto";
import { IntermediaryFooterForm } from "./IntermediaryFooterForm";
import { ExtendedTypography } from "@vapor/react-extended";
import { useTranslation } from "@onefront/react-sdk";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import React from "react";

export interface IntermediaryDataFormProps {
    onSubmit: (data: any) => void;
    onBack: () => void;
    loadingGet?: boolean;
    loadingPost?: boolean;
    mode?: "create" | "edit";
    data?: IntermediaryListDto;
    intermediaries?: Array<IntermediaryListDto>;
}

export const IntermediaryDataForm = ({ onSubmit, onBack, loadingGet, loadingPost, mode, data, intermediaries }) => {
    const [displayErrors, setDisplayErrors] = useState<any>(false);
    const [isEditing, setIsEditing] = useState<any>(mode === "CREATE");
    const [errors, setErrors] = useState<any>(null);
    const [intermediaryData, setIntermediaryData] = useState<any>({ type: "" });
    const [entratelData, setEntratelData] = useState<any>();
    const { intermediaryTaxId } = useParams();

    const { t } = useTranslation();

    const {
        palette: {
            primary: { textSubduedColor: Cadet }
        }
    } = useTheme();

    useEffect(() => {
        if (loadingGet) {
            setIntermediaryData(null);
            setEntratelData(null);
        }
        if (mode === "EDIT" && data && !loadingGet) {
            setIntermediaryData({
                type: data?.type,
                taxId: data?.taxId,
                vatNumber: data?.vatNumber,
                description: data?.description,
                name: data?.details?.name,
                surname: data?.details?.surname
            });
            setEntratelData({
                username: data?.userName,
                pin: data?.pin,
                password: data?.password
            });
        }
    }, [mode, data, intermediaryTaxId, loadingGet]);

    const handleSubmit = () => {
        if (Object.keys(errors).some(key => errors[key])) {
            setDisplayErrors(errors);
        } else {
            onSubmit({
                defaultSignerTaxId: null,
                ...intermediaryData,
                ...entratelData
            });
            if (mode === "EDIT") {
                setIsEditing(false);
            }
        }
    };

    const handleError = useCallback(err => {
        setErrors(prev => {
            return {
                ...prev,
                ...err
            };
        });
    }, []);

    useEffect(() => {
        setDisplayErrors(false);
        if (intermediaryData?.type === "C") {
            setErrors((prev: any) => {
                return {
                    ...prev,
                    password: false
                };
            });
        }
    }, [intermediaryData?.type]);

    const checkDuplicatedTaxId = useMemo(() => {
        if (intermediaries && mode === "CREATE") {
            return intermediaries.some((intermediary: any) => intermediary.taxId === intermediaryData?.taxId);
        }
        return false;
    }, [intermediaries, intermediaryData?.taxId, mode]);

    const checkDuplicatedVatNumber = useMemo(() => {
        if (intermediaries && mode === "CREATE") {
            return intermediaries.some((intermediary: any) => intermediary.vatNumber === intermediaryData?.vatNumber);
        }
        return false;
    }, [intermediaries, intermediaryData?.vatNumber, mode]);

    return (
        <Stack spacing={5} direction="column" maxWidth={780}>
            <Box component={"div"} gap={3} flexDirection={"column"} display={"flex"}>
                <FormHeaderTitle
                    title={mode === "CREATE" ? "Crea Intermediario" : "Dati dell'intermediario"}
                    subtitle="Inserisci informazioni sull'intermediario"
                    //showEdit={mode === "EDIT" && !isEditing}
                    onEdit={() => {
                        setIsEditing(true);
                    }}
                />
                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                    <ExtendedTypography variant="titleSmall" color={Cadet}>
                        Intermediario
                    </ExtendedTypography>
                    {mode === "EDIT" && !isEditing && !loadingPost && !loadingGet && (
                        <Button
                            onClick={() => {
                                setIsEditing(true);
                                setErrors({ ...errors });
                            }}
                            variant="text"
                        >
                            <FontAwesomeIcon icon={faEdit} style={{ marginRight: 8 }} /> {t("general.edit")}
                        </Button>
                    )}
                </Stack>
                <IntermediaryData
                    key="intermediary-data"
                    body={intermediaryData}
                    duplicatedTaxId={checkDuplicatedTaxId}
                    duplicatedVatNumber={checkDuplicatedVatNumber}
                    editing={mode === "CREATE" || (mode === "EDIT" && isEditing)}
                    editableFields={
                        mode === "EDIT"
                            ? {
                                  type: false,
                                  vatNumber: false,
                                  taxId: false
                              }
                            : null
                    }
                    loading={loadingGet || (mode === "EDIT" && loadingPost)}
                    onChange={(data: any, key: any, value: any) => {
                        setDisplayErrors((prev: any) => {
                            return {
                                ...prev,
                                [key]: false
                            };
                        });

                        setIntermediaryData(data);
                        if (key === "type") {
                            setEntratelData(null);
                        }
                    }}
                    onError={handleError}
                    displayErrors={displayErrors}
                    required
                />
            </Box>

            {intermediaryData?.type && (
                <Box>
                    <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} mb={3}>
                        <ExtendedTypography variant="titleSmall" color={Cadet}>
                            Utenza Entratel
                        </ExtendedTypography>
                    </Stack>
                    {isEditing && (
                        <Infobox
                            title="Dove trovo le credenziali da inserire?"
                            description={`Sono le credenziali che usi per accedere al sito dell'agenzia delle entrate (Username o Utenza entratel${
                                intermediaryData?.type === "P" ? ", password" : ""
                            } e pin)`}
                            variant="ghost"
                        />
                    )}
                    <EntratelForm
                        loading={loadingGet || (mode === "EDIT" && loadingPost)}
                        body={{
                            ...entratelData,
                            password: mode === "EDIT" && !isEditing ? "*******" : entratelData?.password,
                            pin: mode === "EDIT" && !isEditing ? "*****" : entratelData?.pin
                        }}
                        onChange={(data: any, key: any, value: any) => {
                            setDisplayErrors((prev: any) => {
                                return {
                                    ...prev,
                                    [key]: false
                                };
                            });

                            setEntratelData(data);
                        }}
                        editing={mode === "CREATE" || (mode === "EDIT" && isEditing)}
                        onError={handleError}
                        displayErrors={displayErrors}
                        showPassword={intermediaryData?.type === "P"}
                        required
                    />
                </Box>
            )}

            {isEditing && (
                <IntermediaryFooterForm
                    onBack={() => {
                        if (mode === "CREATE") {
                            onBack?.();
                        } else {
                            if (mode === "EDIT" && data && !loadingGet) {
                                setIntermediaryData({
                                    type: data?.type,
                                    taxId: data?.taxId,
                                    vatNumber: data?.vatNumber,
                                    description: data?.description,
                                    name: data?.details?.name,
                                    surname: data?.details?.surname
                                });
                                setEntratelData({
                                    username: data?.userName,
                                    pin: data?.pin,
                                    password: data?.password
                                });
                            }
                            setIsEditing(false);
                        }
                    }}
                    onForward={handleSubmit}
                    disabledForward={loadingPost}
                    loading={loadingPost}
                    isNew={mode === "CREATE"}
                    lastStep={false}
                />
            )}
        </Stack>
    );
};
